import Image from 'next/image';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useTailwindBreakpoint } from '@components/Contexts/TailwindBreakpointContext';
import { useLocale } from '@components/Contexts/LocaleContext';
import { useEffect, useState } from 'react';

const PointerSection = () => {
  const { ref, inView } = useInView({ threshold: 0.5, triggerOnce: true });
  const { screenWidth } = useTailwindBreakpoint();
  const [initWidth, setInitWidth] = useState(null);
  const { lang } = useLocale();
  const [isHovering, setIsHovered] = useState(false);
  const [noCursor, setNoCursor] = useState(false);

  useEffect(() => {
    const cursorQuery = window.matchMedia('(pointer: coarse)');
    setNoCursor(cursorQuery.matches);
  }, []);

  useEffect(() => {
    setInitWidth(window.innerWidth);
  }, []);

  useEffect(() => {
    if (screenWidth) {
      setInitWidth(null);
    }
  }, [screenWidth]);

  const onMouseEnter = () => setIsHovered(true);
  const onMouseLeave = () => setIsHovered(false);

  return (
    <section
      id="pointer"
      className={
        initWidth >= 1024 || screenWidth >= 1024 ? 'Container' : 'px-4 md:px-12'
      }
    >
      <div
        ref={ref}
        className="relative flex justify-center min-h-[340px] bg-blue-50 rounded-[30px] items-center p-5 md:p-10 lg:mx-2"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="absolute rounded-tl-[30px] overflow-hidden top-0 left-0">
          <Image
            className="rounded-tl"
            src="images/e-signature/question-mark.svg"
            alt=""
            width={230}
            height={340}
          />
        </div>

        <div className="relative flex flex-col lg:flex-row justify-center items-center">
          <div className="lg:w-[70%] lg:px-9">
            <motion.h1
              className="text-center md:text-start text-h6Semibold gradient-head lg:-ml-4"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              viewport={{ amount: 0.5, once: true }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              {lang === 'en' ? (
                <p>Why select CODIUM&apos;s e-Signature service?</p>
              ) : (
                <p>
                  ทำไมควรเลือกใช้ e-Signature
                  <br className="md:hidden" /> กับโคเดียม?
                </p>
              )}
            </motion.h1>

            <motion.ul
              role="list"
              className="list-disc pt-5 text-p3Regular marker:text-[10px] pl-4 md:pl-0"
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : { opacity: 0 }}
              viewport={{ amount: 0.5, once: true }}
              transition={{ duration: 1, delay: 0.5 }}
            >
              <li className="pb-2">
                <span className="text-p3Semibold text-lightBlue">
                  One-Stop Service:
                </span>{' '}
                {lang == 'en' ? (
                  <>
                    We provide comprehensive solutions for businesses of all
                    sizes, from emerging startups to established enterprises,
                    with a deep understanding of real business needs.
                  </>
                ) : (
                  <>
                    พร้อมตอบโจทย์ทุกรูปแบบการใช้งาน
                    ไม่ว่าจะเป็นองค์กรขนาดเล็กที่กำลังเติบโต
                    หรือองค์กรขนาดใหญ่ที่มีพนักงานหลายพันคน{' '}
                    <span className="text-p3Semibold text-lightBlue">
                      เข้าใจถึงความต้องการของธุรกิจอย่างแท้จริง
                    </span>
                  </>
                )}
              </li>
              <li className="text-p3Semibold pb-2">
                <span className=" text-lightBlue">Flexible & Versatile:</span>{' '}
                {lang == 'en' ? (
                  <>
                    Beyond our electronic signature system, we offer a suite of
                    integrated solutions designed to streamline and transform
                    your organization&apos;s workflow.
                  </>
                ) : (
                  <>
                    นอกจากระบบลงลายมือชื่ออิเล็กทรอนิกส์แล้ว
                    เรายังมีระบบอื่นๆที่สามารถทำงานร่วมกัน มาช่วย Transform
                    การทำงานขององค์กรอย่างเต็มรูปแบบ
                  </>
                )}
              </li>
              <li className="pb-2">
                <span className="text-p3Semibold text-lightBlue">
                  Professional Team:
                </span>{' '}
                {lang == 'en' ? (
                  <>
                    Our experienced experts have supported leading private
                    enterprises, international organizations, and government
                    agencies, showcasing our proven expertise and reliability.
                  </>
                ) : (
                  <>
                    ช่วยให้คำปรึกษาแก่ผู้ใช้งานมาแล้วกว่าร้อยองค์กร
                    ทั้งองค์กรเอกชนในประเทศ, ต่างประเทศ และหน่วยงานราชการ
                  </>
                )}
              </li>
            </motion.ul>
          </div>

          <motion.div
            className={`flex ${
              isHovering || noCursor ? 'lg:filter-none' : 'lg:grayscale'
            }`}
            initial={{ opacity: 0, scale: 1 }}
            animate={
              inView
                ? {
                    opacity: 1,
                    scale: [1, 1.2, 1],
                    transition: { type: 'spring', delay: 0.5, duration: 1 },
                  }
                : { opacity: 0, scale: 1 }
            }
          >
            <Image
              src={
                lang === 'en'
                  ? 'images/e-signature/pointer-eng.webp'
                  : 'images/e-signature/pointer.webp'
              }
              alt="ตอบโจทย์ทุกองค์การด้วย e-signature"
              width={460}
              height={276}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PointerSection;
